import type { LucideIcon } from "lucide-react";
import { CalendarClock, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPlanMoment } from "@/lib/billing/formatPlanMoment";
import {
  resolvePlanDates,
  type PlanDateKind,
  type PlanDatesSubscriptionLike,
} from "@/lib/billing/resolvePlanDates";
import { cn } from "@/lib/utils";

interface PlanRenewalDateProps {
  subscription: PlanDatesSubscriptionLike | null | undefined;
  className?: string;
}

const PLAN_KEYS: Record<PlanDateKind, string> = {
  renews: "billing.planRenewsOn",
  expires: "billing.planExpiresOn",
  partner: "billing.partnerUntil",
};

interface Line {
  id: "plan" | "usage" | "both";
  icon: LucideIcon;
  text: string;
}

/**
 * The plan card's dates: when the plan renews (or expires / the partner grant
 * ends) and when clips and processing time reset. Monthly Stripe and PIX reset
 * when the payment lands, so they share one line with the renewal.
 */
export function PlanRenewalDate({ subscription, className }: PlanRenewalDateProps) {
  const { t, i18n } = useTranslation();
  const { reset, plan, resetsOnPayment, awaitingPayment } = resolvePlanDates(subscription, new Date());
  const format = (moment: string) => formatPlanMoment(moment, i18n?.language);

  const lines: Line[] = [];
  if (resetsOnPayment && plan?.kind === "renews") {
    lines.push({
      id: "both",
      icon: CalendarClock,
      text: t("billing.planAndUsageRenewOn", { date: format(plan.at) }),
    });
  } else {
    if (plan) {
      lines.push({ id: "plan", icon: CalendarClock, text: t(PLAN_KEYS[plan.kind], { date: format(plan.at) }) });
    }
    if (awaitingPayment) {
      lines.push({ id: "usage", icon: RefreshCw, text: t("billing.usageResetsOnPayment") });
    } else if (reset) {
      lines.push({ id: "usage", icon: RefreshCw, text: t("billing.usageResetsOn", { date: format(reset) }) });
    }
  }
  if (lines.length === 0) return null;

  return (
    <div
      data-testid="plan-renewal-date"
      className={cn("flex flex-col gap-0.5 text-xs text-muted-foreground", className)}
    >
      {lines.map(({ id, icon: Icon, text }) => (
        <p key={id} data-testid={`plan-date-${id}`} className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{text}</span>
        </p>
      ))}
    </div>
  );
}
